import React from 'react';
import styled from 'styled-components';
import * as RadixRadioGroup from '@radix-ui/react-radio-group';
import classNames from 'classnames';
import { colors } from '../../style';
import { BoxProps, Flex } from '../box';
import Radio, { RadioSize } from '../radio';
import Text, { TextProps } from '../text';

// RadioSize is sm | md and both are valid Text sizes, so the same prop drives both.
type OptionSize = RadioSize & TextProps['size'];

export interface RadioGroupProps {
  value: string;
  onChange(value: string): void;
  disabled?: boolean | undefined;
  children: React.ReactNode;
  'aria-labelledby'?: string;
  id?: string;
  as?: React.ElementType;

  gap?: BoxProps['gap'];
}

interface RadioGroupContextValue {
  value: string;
  disabled?: boolean;
}

const RadioGroupContext = React.createContext<RadioGroupContextValue | null>(null);

const RadioGroup = ({ onChange, value, disabled, children, id, ...rest }: RadioGroupProps) => {
  return (
    <RadioGroupContext value={{ value, disabled }}>
      <RadixRadioGroup.Root onValueChange={onChange} value={value} disabled={disabled} id={id} {...rest}>
        {children}
      </RadixRadioGroup.Root>
    </RadioGroupContext>
  );
};

interface RadioOptionProps {
  id?: string;
  label?: React.ReactNode | string;
  'data-testid'?: string;
  ariaLabel?: string;
  description?: React.ReactNode | string;
  value?: string;
  disabled?: boolean;
  weight?: TextProps['weight'];
  size?: OptionSize;
}

const RadioOption = ({
  value = '',
  label,
  description,
  ariaLabel = '',
  weight,
  size,
  disabled,
  ...rest
}: RadioOptionProps) => {
  const [active, setActive] = React.useState(false);
  const context = React.use(RadioGroupContext);

  const isDisabled = disabled || context?.disabled;
  const isChecked = context?.value === value;

  return (
    <RadixRadioGroup.Item asChild value={value} disabled={isDisabled} {...rest}>
      <RadioOptionContainer
        className={classNames('radio-option-container', { disabled: isDisabled })}
        aria-label={ariaLabel}
        aria-disabled={isDisabled}
        aria-checked={isChecked}
        onFocus={() => setActive(true)}
        onBlur={() => setActive(false)}
      >
        <Radio checked={isChecked} disabled={isDisabled} active={active} size={size} />

        <Flex className={'label-and-description'} flexDirection={'column'}>
          {label && (
            <Text size={size} weight={weight} componentClass={'span'}>
              {label}
            </Text>
          )}

          {description && (
            <Text componentClass={'span'} variant={'gray600'} size={'md'}>
              {description}
            </Text>
          )}
        </Flex>
      </RadioOptionContainer>
    </RadixRadioGroup.Item>
  );
};

const RadioOptionContainer = styled(Flex)`
  align-items: center;

  &:focus {
    outline: none !important;
  }

  &.radio-option-container {
    * {
      cursor: pointer;
    }
    &.disabled {
      * {
        cursor: not-allowed;
        pointer-events: none;
      }
      span {
        color: ${colors.gray600};
      }
    }
  }
`;

RadioGroup.Option = RadioOption;

export { RadioGroup };
